import { transitions } from '@leadsales/design-system/motion';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { randomString } from '../../functions/string.functions';
import { initialState } from './Toast.constants';
import { ToastContext } from './Toast.context';
import { tAlert, tToastAlert, tToastContext } from './Toast.types';

// How long a closing toast stays mounted: the DS notify exit
// (--ls-motion-notify-exit), the same token its CSS animation plays with
// (src/styles/vendor/daisyui.motion.scss). Reduced motion keeps it: the toast
// fades out in place instead of travelling, in the same time (DES-210).
export const TOAST_EXIT_MS = Math.round(transitions.notifyExit.duration * 1000);

export function useToastContext(): tToastContext {
  return useContext(ToastContext);
}

export default function useToast(): tToastContext {
  const [alerts, setAlerts] = useState<tToastAlert[]>(initialState.alerts);
  // One removal per leaving toast, keyed by its id: closing it twice (the
  // button during the exit, or its own timer) schedules nothing more.
  const exitTimers = useRef(new Map<string, number>());

  useEffect(() => {
    const timers = exitTimers.current;
    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, []);

  const addAlert = useCallback(function (alert: tAlert) {
    const toast = { ...alert, id: randomString() };
    setAlerts((prevAlerts) => [...prevAlerts, toast]);
    return toast;
  }, []);

  const deleteAlert = useCallback(function (alert: tToastAlert) {
    const timers = exitTimers.current;
    if (timers.has(alert.id)) return;

    setAlerts((prevAlerts) =>
      prevAlerts.map((a) => (a.id === alert.id ? { ...a, exiting: true } : a)),
    );
    timers.set(
      alert.id,
      window.setTimeout(() => {
        timers.delete(alert.id);
        setAlerts((prevAlerts) => prevAlerts.filter((a) => a.id !== alert.id));
      }, TOAST_EXIT_MS),
    );
  }, []);

  return {
    alerts,
    addAlert,
    deleteAlert,
  };
}
